import axios from 'axios';
import { API_URL } from '../../config/api';
import SessionService from '../../services/SessionService';

/** Assistente de primeiro acesso (/comecar) e "montar a base" — backend em /api/onboarding. */

export type StepState = 'pending' | 'running' | 'done' | 'skipped' | 'failed';
export type BaseBuildStatus = 'estimated' | 'running' | 'paused' | 'done' | 'failed' | 'undoing' | 'undone' | 'discarded';
export type OnboardingStep = 'whatsapp' | 'email' | 'calendar' | 'base';

export interface BaseBuildStepProgress {
  state: StepState;
  done: number;
  total: number;
  note?: string | null;
}

export interface BaseBuildEstimate {
  channel: 'whatsapp_qr' | 'whatsapp_official' | 'none';
  windowDays: number;
  historyAvailable: boolean;
  historyNote?: string | null;
  chats: number;
  estimatedMessages: number;
  existingContacts: number;
  contactsWithConversation: number;
  projectedConversations: number;
  aiCalls: number;
  minutes: number;
  funnelReady: boolean;
  funnelTemplate?: string | null;
}

export interface BaseBuildJob {
  id: number;
  sectorId: number;
  status: BaseBuildStatus;
  channel: string;
  windowDays: number;
  currentStep?: string | null;
  steps: Record<string, BaseBuildStepProgress>;
  estimate?: BaseBuildEstimate | null;
  counts: Record<string, number>;
  error?: string | null;
  createdAt: string;
  confirmedAt?: string | null;
  finishedAt?: string | null;
  undoneAt?: string | null;
}

export interface OnboardingSector {
  sectorId: number;
  name: string;
  isOfficial: boolean;
  sparkActive: boolean;
  whatsAppConnected: boolean;
  contacts: number;
  cards: number;
  baseReady: boolean;
  latestJob?: BaseBuildJob | null;
  sparkHistorySyncedAt?: string | null;
}

export interface OnboardingStatus {
  sectors: OnboardingSector[];
  needsOnboarding: boolean;
}

export interface DiscoveredClient {
  contactId: number;
  name: string;
  phoneMasked?: string | null;
  createdByImport: boolean;
  messagesImported: number;
  summary?: string | null;
  cardId?: number | null;
  columnName?: string | null;
  columnKind?: string | null;
  lastCustomerMessage?: string | null;
  lastMessageAt?: string | null;
}

export interface DiscoveredNegotiation {
  contactId: number;
  name: string;
  cardId: number;
  fromColumn?: string | null;
  toColumn?: string | null;
  title?: string | null;
  value?: number | null;
  currency?: string | null;
  evidence?: string | null;
  confidence?: string | null;
  source: 'moved' | 'opened' | 'opportunity';
}

export interface DiscoveredConversation {
  contactId: number;
  name: string;
  messagesImported: number;
  note?: string | null;
}

export interface BaseBuildDiscoveries {
  job: BaseBuildJob;
  clients: DiscoveredClient[];
  conversations: DiscoveredConversation[];
  negotiations: DiscoveredNegotiation[];
}

/** Algo que a montagem criou e o desfazer manteve porque foi usado depois (motivo estruturado do backend). */
export interface BaseBuildUndoKept {
  kind: 'card' | 'contact';
  entityId: number;
  contactId?: number | null;
  reason: string;
}

export interface BaseBuildUndoResult {
  messagesRemoved: number;
  contactsRemoved: number;
  cardsRemoved: number;
  movesReverted: number;
  summariesRestored: number;
  opportunitiesRestored: number;
  cardsKept?: number;
  contactsKept?: number;
  kept?: BaseBuildUndoKept[] | null;
}

/** Reler conversas para a memória: só memória, não envia mensagem nem mexe no funil. */
export type MemoryBackfillStatus = 'estimated' | 'running' | 'done' | 'cancelled' | 'failed';

export interface MemoryBackfillEstimate {
  windowDays: number;
  conversations: number;
  chunks: number;
  messages: number;
  mediaToTranscribe: number;
  aiCalls: number;
}

export interface MemoryBackfillJob {
  id: number;
  sectorId: number;
  status: MemoryBackfillStatus;
  windowDays: number;
  estimate?: MemoryBackfillEstimate | null;
  processedWindows: number;
  savedItems: number;
  error?: string | null;
  createdAt: string;
  confirmedAt?: string | null;
  finishedAt?: string | null;
}

const base = `${API_URL}/onboarding`;

function headers() {
  return { Accept: 'application/json', Authorization: `Bearer ${SessionService.getToken()}` };
}

/**
 * @param timeoutMs Limite da consulta (o login usa para nunca ficar preso esperando o servidor). Sem valor = sem limite.
 */
export async function fetchOnboardingStatus(timeoutMs?: number): Promise<OnboardingStatus> {
  const res = await axios.get<OnboardingStatus>(`${base}/status`, timeoutMs ? { headers: headers(), timeout: timeoutMs } : { headers: headers() });
  return { sectors: res.data?.sectors ?? [], needsOnboarding: res.data?.needsOnboarding ?? false };
}

export async function fetchLatestBaseBuild(sectorId: number): Promise<BaseBuildJob | null> {
  const res = await axios.get<BaseBuildJob>(`${base}/sectors/${sectorId}/latest`, { headers: headers() });
  return res.status === 204 || !res.data ? null : res.data;
}

export async function estimateBaseBuild(sectorId: number, windowDays = 90): Promise<BaseBuildJob> {
  const res = await axios.post<BaseBuildJob>(`${base}/estimate`, { sectorId, windowDays }, { headers: headers() });
  return res.data;
}

export async function fetchBaseBuild(jobId: number): Promise<BaseBuildJob> {
  const res = await axios.get<BaseBuildJob>(`${base}/jobs/${jobId}`, { headers: headers() });
  return res.data;
}

export async function confirmBaseBuild(jobId: number, useFunnelTemplate = true): Promise<BaseBuildJob> {
  const res = await axios.post<BaseBuildJob>(`${base}/jobs/${jobId}/confirm`, { useFunnelTemplate }, { headers: headers() });
  return res.data;
}

export async function pauseBaseBuild(jobId: number): Promise<BaseBuildJob> {
  const res = await axios.post<BaseBuildJob>(`${base}/jobs/${jobId}/pause`, {}, { headers: headers() });
  return res.data;
}

export async function resumeBaseBuild(jobId: number): Promise<BaseBuildJob> {
  const res = await axios.post<BaseBuildJob>(`${base}/jobs/${jobId}/resume`, {}, { headers: headers() });
  return res.data;
}

export async function fetchDiscoveries(jobId: number, take = 300): Promise<BaseBuildDiscoveries> {
  const res = await axios.get<BaseBuildDiscoveries>(`${base}/jobs/${jobId}/discoveries`, { params: { take }, headers: headers() });
  return res.data;
}

export async function undoBaseBuild(jobId: number): Promise<BaseBuildUndoResult> {
  const res = await axios.post<BaseBuildUndoResult>(`${base}/jobs/${jobId}/undo`, {}, { headers: headers() });
  return res.data;
}

export async function estimateMemoryBackfill(sectorId: number, windowDays = 30): Promise<MemoryBackfillJob> {
  const res = await axios.post<MemoryBackfillJob>(`${base}/memory-backfill/estimate`, { sectorId, windowDays }, { headers: headers() });
  return res.data;
}

export async function confirmMemoryBackfill(id: number): Promise<MemoryBackfillJob> {
  const res = await axios.post<MemoryBackfillJob>(`${base}/memory-backfill/${id}/confirm`, {}, { headers: headers() });
  return res.data;
}

export async function cancelMemoryBackfill(id: number): Promise<MemoryBackfillJob> {
  const res = await axios.post<MemoryBackfillJob>(`${base}/memory-backfill/${id}/cancel`, {}, { headers: headers() });
  return res.data;
}

export async function fetchMemoryBackfill(id: number): Promise<MemoryBackfillJob> {
  const res = await axios.get<MemoryBackfillJob>(`${base}/memory-backfill/${id}`, { headers: headers() });
  return res.data;
}

/** Prévia aguardando confirmação ou job em execução da conexão (null = nenhum). */
export async function fetchActiveMemoryBackfill(sectorId: number): Promise<MemoryBackfillJob | null> {
  const res = await axios.get<MemoryBackfillJob>(`${base}/memory-backfill/active`, { params: { sectorId }, headers: headers() });
  return res.status === 204 || !res.data ? null : res.data;
}

/** Evento onboarding_step_completed (PostHog). Nunca derruba a tela. */
export async function reportOnboardingStep(step: OnboardingStep, sectorId: number, detail?: string): Promise<void> {
  try {
    await axios.post(`${base}/steps/${step}/complete`, { sectorId, detail }, { headers: headers() });
  } catch {
    // telemetria é opcional
  }
}

/** Para onde o retorno do OAuth / Meta (que sempre cai em /sectors) deve voltar. */
export const ONBOARDING_RETURN_KEY = 'mends.onboarding.returnTo';

export function rememberOnboardingReturn(path = '/comecar') {
  try {
    window.localStorage.setItem(ONBOARDING_RETURN_KEY, path);
  } catch {
    // armazenamento bloqueado: o usuário volta pelo menu
  }
}

export function forgetOnboardingReturn() {
  try {
    window.localStorage.removeItem(ONBOARDING_RETURN_KEY);
  } catch {
    // ignore
  }
}

export const STEP_LABELS: Record<string, string> = {
  contacts: 'Contatos',
  history: 'Histórico de conversas',
  cards: 'Cards no funil',
  funnel: 'Posição no funil',
  insights: 'Resumo e negociações',
};

/** Motivos estruturados do backend em linguagem de gente. */
export function noteText(note?: string | null): string | null {
  if (!note) return null;
  if (note.startsWith('chat_error:')) {
    return `${note.split(':')[1]} conversa(s) não vieram por erro do WhatsApp. Monte de novo: o que já veio não é duplicado.`;
  }
  if (note.startsWith('phone_no_response:')) {
    const n = note.split(':')[1];
    return `${n} conversa(s): o celular não respondeu ao pedido de mensagens antigas. Deixe o WhatsApp aberto no celular e monte de novo para buscar mais.`;
  }
  const map: Record<string, string> = {
    official_no_history_api: 'WhatsApp oficial não entrega histórico anterior à conexão: a base usa as conversas que chegarem a partir de agora.',
    official_contacts_from_conversations: 'No WhatsApp oficial, os contatos chegam com as conversas.',
    source_unavailable: 'WhatsApp não está conectado agora: usamos os contatos e conversas que já estão na MENDS.',
    source_error: 'Não deu para consultar o WhatsApp agora: usamos o que já está na MENDS.',
    partial_retention: 'O WhatsApp guarda poucos dias no servidor; o restante é pedido ao celular durante a montagem.',
    funnel_without_conditions: 'O funil não tem condições nas colunas: os cards ficaram na entrada. Configure o funil e reavalie.',
    funnel_automation_off: 'A automação do funil está desligada nesta conta.',
    no_history_source: 'Esta conexão não tem histórico para importar.',
  };
  return map[note] ?? null;
}

/** Retorno do Google/Microsoft com erro: códigos OAuth conhecidos em português; o resto vira uma mensagem genérica. */
export function oauthErrorText(code?: string | null): string {
  const map: Record<string, string> = {
    access_denied: 'A conexão foi cancelada ou a permissão foi negada. Tente de novo e aceite o acesso pedido.',
    consent_required: 'É preciso aceitar as permissões pedidas para conectar. Tente de novo.',
    interaction_required: 'A conta pediu uma confirmação extra. Tente de novo e conclua o login.',
    login_required: 'A sessão na conta expirou. Tente de novo e entre na conta.',
    admin_consent_required: 'O administrador da sua empresa precisa liberar o acesso da MENDS a esta conta.',
    unauthorized_client: 'Esta conta não permite conectar a MENDS. Fale com o administrador da sua empresa.',
    invalid_scope: 'A conta não aceitou as permissões pedidas. Fale com o suporte da MENDS.',
    invalid_request: 'O pedido de conexão não foi aceito. Tente de novo.',
    invalid_grant: 'O link de conexão expirou. Tente de novo.',
    server_error: 'O provedor da conta teve um erro. Tente de novo em alguns minutos.',
    temporarily_unavailable: 'O provedor da conta está indisponível agora. Tente de novo em alguns minutos.',
  };
  const key = (code ?? '').trim().toLowerCase();
  return map[key] ?? 'Não foi possível conectar a conta. Tente de novo.';
}

/** Motivo de o desfazer ter mantido um card/cliente, em linguagem de gente. */
export function keptReasonText(reason: string): string {
  const map: Record<string, string> = {
    card_moved: 'card movido depois da importação',
    card_edited: 'card editado depois da importação',
    card_closed: 'card marcado como ganho ou perdido',
    card_activity: 'card movido pela automação ou por alguém',
    contact_has_messages: 'cliente com conversa nova',
    contact_has_cards: 'cliente com card em uso',
    contact_edited: 'cliente editado depois da importação',
  };
  return map[reason] ?? 'usado depois da importação';
}

export function isActiveJob(job?: BaseBuildJob | null): boolean {
  return !!job && (job.status === 'running' || job.status === 'paused' || job.status === 'undoing');
}
